import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import type { ExistingOrganisation, NewOrganisation } from './types'

interface OrganisationsPanelProps {
  newOrganisations: readonly NewOrganisation[]
  existingOrganisations: readonly ExistingOrganisation[]
  /** Give the rows of a new organisation another name (and check them again). */
  onRename: (rows: number[], name: string) => void
}

const rowsLabel = (count: number) => (count === 1 ? '1 rij' : `${count} rijen`)

function NewOrganisationLine({
  organisation,
  mergeTargets,
  onRename,
}: {
  organisation: NewOrganisation
  mergeTargets: readonly string[]
  onRename: (rows: number[], name: string) => void
}) {
  const [draft, setDraft] = useState(organisation.name)
  function commit() {
    const name = draft.trim()
    if (name && name !== organisation.name) onRename(organisation.rows, name)
    else setDraft(organisation.name)
  }
  return (
    <li className="flex flex-wrap items-center gap-2 py-2">
      <input
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
        }}
        aria-label={`Naam van nieuwe organisatie ${organisation.name}`}
        className="h-8 w-64 rounded-md border border-border bg-background px-2 text-sm"
      />
      <span className="text-sm text-muted-foreground">{rowsLabel(organisation.rows.length)}</span>
      {mergeTargets.length > 0 ? (
        <select
          aria-label={`Samenvoegen met, ${organisation.name}`}
          value=""
          onChange={(event) => {
            if (event.target.value) onRename(organisation.rows, event.target.value)
          }}
          className="h-8 w-56 rounded-md border border-border bg-background px-2 text-sm"
        >
          <option value="">Samenvoegen met…</option>
          {mergeTargets.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      ) : null}
    </li>
  )
}

/**
 * "Organisaties: n bestaand · m nieuw": the organisations of the file. A new one can be
 * renamed (its rows follow) or merged with another organisation of the file or an existing
 * one, which gives its rows that name.
 */
export function OrganisationsPanel({
  newOrganisations,
  existingOrganisations,
  onRename,
}: OrganisationsPanelProps) {
  const [open, setOpen] = useState(false)
  if (newOrganisations.length + existingOrganisations.length === 0) return null
  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="self-start"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? <ChevronDown /> : <ChevronRight />}
        {`Organisaties: ${existingOrganisations.length} bestaand · ${newOrganisations.length} nieuw`}
      </Button>
      {open ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
          {newOrganisations.length > 0 ? (
            <section aria-label="Nieuwe organisaties">
              <h3 className="text-sm font-semibold text-foreground">Nieuw</h3>
              <ul className="flex flex-col divide-y divide-border">
                {newOrganisations.map((organisation) => (
                  <NewOrganisationLine
                    key={`${organisation.key}:${organisation.name}`}
                    organisation={organisation}
                    mergeTargets={[
                      ...newOrganisations.filter((item) => item !== organisation).map((item) => item.name),
                      ...existingOrganisations.map((item) => item.name),
                    ]}
                    onRename={onRename}
                  />
                ))}
              </ul>
            </section>
          ) : null}
          {existingOrganisations.length > 0 ? (
            <section aria-label="Bestaande organisaties">
              <h3 className="text-sm font-semibold text-foreground">Bestaand</h3>
              <ul className="flex flex-col text-sm">
                {existingOrganisations.map((organisation) => (
                  <li key={organisation.id} className="flex gap-2 py-1">
                    <span className="text-foreground">{organisation.name}</span>
                    <span className="text-muted-foreground">{rowsLabel(organisation.rows.length)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
